import {Trans} from '@lingui/macro';
import _, {noop} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {ChannelCell} from '../../components/common/table/cells/channelCell';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
  virtualTableRowOffset,
  VirtualTableSortableColumnDirectionsEnum,
} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {SelectedColumnSort} from '../../components/common/table/tableColumn';
import {TableRow} from '../../components/common/table/tableRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {ChannelModel} from '../../models/api/channelModel';
import {InboxModel} from '../../models/api/inboxModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {SettingsContentHeader} from '../common/settingsContentHeader';
import {SettingsResourcesTableColumnsEnum} from '../common/settingsResourcesCommon';
import {StyledSettingsContent, StyledSettingsMain, StyledSettingsWrapper} from '../common/settingsWrapper';
import {useFetchChannels} from '../fetchers/channels';
import {SettingsInboxesInboxHeader} from './settingsInboxesInboxHeader';

/*
 * Props.
 */

interface SettingsInboxesChannelsProps {
  url: SettingsInboxesUrl;
  inbox: InboxModel;
}

/*
 * Style.
 */

const StyledCopyDiv = styled.div`
  padding: 0 48px 24px;
`;

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  padding-left: ${virtualTableRowCheckboxWrapperWidth}px;
`;

const StyledTableRow = styled(TableRow)`
  min-height: 56px;
  background: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

const columns = [
  {
    id: SettingsResourcesTableColumnsEnum.NAME,
    type: SettingsResourcesTableColumnsEnum.NAME,
    title: <Trans id="settingstable-name">Name</Trans>,
    cellType: VirtualTableColumnCellsEnum.TEXT,
    settings: {
      cellWidth: {
        shouldFillRemainingWidth: true,
        minWidth: 330 - virtualTableRowOffset,
      },
    },
  },
];

export const SettingsInboxesChannels: FC<SettingsInboxesChannelsProps> = (props) => {
  const {url, inbox} = props;

  const [searchQuery, setSearchQuery] = useState('');
  const {channels, status} = useFetchChannels(inbox.id);
  const {navigateToLink} = useLinkNavigation();
  const [sortedColumn, setSortedColumn] = useState<SelectedColumnSort>({
    direction: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
    id: SettingsResourcesTableColumnsEnum.NAME,
  });

  const channelsForTable = useMemo(
    () =>
      _(channels)
        .filter(
          (c) =>
            c.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.typeName.toLowerCase().includes(searchQuery.toLowerCase()),
        )
        .orderBy((c) => c.displayName, sortedColumn.direction)
        .value(),
    [channels, searchQuery, sortedColumn.direction],
  );

  const renderRow: VirtualTableRowRenderer<ChannelModel> = (item, options) => (
    <StyledTableRow
      hasHover
      onSelect={() => {
        navigateToLink(buildSettingsPath({...url, channelId: item.id}));
      }}
      renderColumn={(isHovering) => (
        <ChannelCell value={item} settings={columns[0].settings} index={0} isHovering={isHovering} />
      )}
    />
  );

  return (
    <StyledSettingsWrapper>
      <SettingsInboxesInboxHeader url={url} inbox={inbox} />
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledCopyDiv>
            <Trans id="settingschannels-trafficopy">
              Emails, tweets, sms and other communication received in this inbox.
            </Trans>
          </StyledCopyDiv>
          <SettingsContentHeader
            searchInputValue={searchQuery}
            onSearchInput={setSearchQuery}
            onExitClick={() => setSearchQuery('')}
            renderButtons={(buttonProps) => (
              <Button
                {...buttonProps}
                variant={ButtonVariantsEnum.PRIMARY}
                onClick={() => {
                  navigateToLink(
                    buildSettingsPath({
                      ...url,
                      subPage: SettingsInboxesSubPagesEnum.NEW_CHANNEL,
                      resourcePage: SettingsResourcePagesEnum.EDIT,
                    }),
                  );
                }}
              >
                <Trans id="settingstags-addachannel">Add a channel</Trans>
              </Button>
            )}
          />
          <StyledTableContainerDiv>
            <StaticVirtualTable<ChannelModel>
              items={channelsForTable}
              shouldRenderEmptyState={channels.length === 0}
              onLoadMore={noop}
              columns={columns}
              sortableColumns={[
                {
                  id: SettingsResourcesTableColumnsEnum.NAME,
                  directions: [
                    VirtualTableSortableColumnDirectionsEnum.ASCENDING,
                    VirtualTableSortableColumnDirectionsEnum.DESCENDING,
                  ],
                },
              ]}
              sortedColumn={sortedColumn}
              onSortUpdate={setSortedColumn}
              renderRow={renderRow}
              hasData={channels.length > 0}
              moreStatuses={{
                [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
                [ScrollDirectionsEnum.BOTTOM]:
                  status === AsyncStatusesEnum.LOADING
                    ? AsyncMoreStatusesEnum.LOADING
                    : AsyncMoreStatusesEnum.ALL_LOADED,
              }}
            />
          </StyledTableContainerDiv>
        </StyledSettingsContent>
      </StyledSettingsMain>
    </StyledSettingsWrapper>
  );
};
